import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/axios';
import remountOnId from '../utils/remountOnId';
import toast from 'react-hot-toast';
import SearchableSelect from '../components/SearchableSelect';
import { formatStatusText } from '../utils/statusUtils';
import DocumentSubsection from '../components/DocumentSubsection';
import Dropdown from '../components/Dropdown';
import { COUNTRY_OPTIONS } from '../constants/countries';
import { EMPLOYMENT_TYPES } from '../constants';
import { companyToday, formatDateOnly } from '../utils/dateUtils';


const getOnboardingBadgeClass = (status) => {
  const map = {
    pending_review: 'bg-warning-100 text-warning-800',
    in_review: 'bg-blue-100 text-blue-800',
    approved: 'bg-success-100 text-success-800',
    rejected: 'bg-danger-100 text-danger-800',
  };
  return map[status] || 'bg-gray-100 text-gray-800';
};

const Field = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900">{value || <span className="text-gray-400 italic">Not provided</span>}</dd>
  </div>
);

const OnboardingReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showSalaryFields, setShowSalaryFields] = useState(false);

  const [personalForm, setPersonalForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', cnic: '',
    date_of_birth: '', gender: '', address: '', city: '', country: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  });
  const [savingPersonal, setSavingPersonal] = useState(false);
  const todayStr = companyToday();
  const [hrForm, setHrForm] = useState({
    position: '', department_id: '', manager_id: '',
    hire_date: '', employment_type: '', taxable: false,
  });
  const [salaryForm, setSalaryForm] = useState({
    amount: '', currency_id: '', effective_date: '',
    payment_method_id: '', bank_id: '', account_number: '', account_title: '',
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    // Only the latest run may pre-fill the forms: StrictMode runs this effect twice in
    // dev (and an id change starts a new load), and a late response would overwrite
    // what the reviewer has typed since.
    let cancelled = false;
    const fetchAll = async () => {
      try {
        // Request + audit are required; reference-data calls degrade gracefully
        // so a 403 on one lookup table doesn't kill the whole page.
        const [reqRes, auditRes] = await Promise.all([
          api.get(`/api/onboarding/${id}`),
          api.get(`/api/onboarding/${id}/audit`),
        ]);
        const [deptRes, empRes, curRes, pmRes, bankRes] = await Promise.allSettled([
          api.get('/api/departments'),
          api.get('/api/employees?limit=500&status=active'),
          api.get('/api/currencies'),
          api.get('/api/payment-methods'),
          api.get('/api/banks'),
        ]);
        if (cancelled) return;
        const unwrap = (settled) => {
          if (settled.status !== 'fulfilled') return [];
          const d = settled.value.data;
          return d?.data ?? d ?? [];
        };

        const req = reqRes.data.data;
        setRequest(req);
        setAuditLogs(auditRes.data.data || []);
        setDepartments(unwrap(deptRes));
        setEmployees(unwrap(empRes));
        setCurrencies(unwrap(curRes));
        setPaymentMethods(unwrap(pmRes));
        setBanks(unwrap(bankRes));

        // Pre-fill personal form from existing data
        setPersonalForm({
          first_name: req.first_name || '', last_name: req.last_name || '',
          email: req.email || '', phone: req.phone || '',
          cnic: req.cnic || '',
          date_of_birth: req.date_of_birth || '',
          gender: req.gender || '',
          address: req.address || '', city: req.city || '', country: req.country || '',
          emergency_contact_name: req.emergency_contact_name || '',
          emergency_contact_phone: req.emergency_contact_phone || '',
          emergency_contact_relationship: req.emergency_contact_relationship || '',
        });

        // Pre-fill HR form from existing data
        setHrForm({
          position: req.position || '',
          department_id: req.department_id ? String(req.department_id) : '',
          manager_id: req.manager_id ? String(req.manager_id) : '',
          hire_date: req.hire_date || '',
          employment_type: req.employment_type || '',
          taxable: req.taxable || false,
        });

        if (req.salary_data) {
          setSalaryForm({
            amount: req.salary_data.amount || '',
            currency_id: req.salary_data.currency_id ? String(req.salary_data.currency_id) : '',
            effective_date: req.salary_data.effective_date || '',
            payment_method_id: req.salary_data.payment_method_id ? String(req.salary_data.payment_method_id) : '',
            bank_id: req.salary_data.bank_id ? String(req.salary_data.bank_id) : '',
            account_number: req.salary_data.account_number || '',
            account_title: req.salary_data.account_title || '',
          });
          setShowSalaryFields(true);
        }
        if (Array.isArray(req.documents_data)) {
          setDocuments(req.documents_data);
        }
      } catch (err) {
        if (cancelled) return;
        toast.error('Failed to load request details');
        navigate('/onboarding/requests');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const setHr = (field) => (e) => {
    const value = e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setHrForm(prev => ({ ...prev, [field]: value }));
  };

  const setSal = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setSalaryForm(prev => ({ ...prev, [field]: value }));
  };

  const setPersonal = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setPersonalForm(prev => ({ ...prev, [field]: value }));
  };

  const formatCNIC = (value) => {
    let clean = value.replace(/[^0-9]/g, '');
    if (clean.length > 13) clean = clean.slice(0, 13);
    let formatted = clean;
    if (clean.length > 5) formatted = clean.slice(0, 5) + '-' + clean.slice(5);
    if (clean.length > 12) formatted = formatted.slice(0, 13) + '-' + formatted.slice(13);
    return formatted;
  };

  const handleSavePersonal = async () => {
    if (personalForm.date_of_birth && personalForm.date_of_birth > todayStr) {
      toast.error('Date of birth cannot be in the future');
      return;
    }
    setSavingPersonal(true);
    try {
      const res = await api.put(`/api/onboarding/${id}/personal-data`, personalForm);
      setRequest(res.data.data);
      toast.success('Personal information updated');
      const auditRes = await api.get(`/api/onboarding/${id}/audit`);
      setAuditLogs(auditRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update personal information');
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleSaveHRData = async () => {
    setSaving(true);
    try {
      const payload = {
        ...hrForm,
        department_id: hrForm.department_id ? parseInt(hrForm.department_id) : null,
        manager_id: hrForm.manager_id ? parseInt(hrForm.manager_id) : null,
        hire_date: hrForm.hire_date || null,
        documents_data: documents.filter(d => d.document_name && d.document_type && d.google_drive_link),
      };
      if (showSalaryFields && salaryForm.amount && salaryForm.currency_id) {
        const selectedPm = paymentMethods.find(pm => String(pm.id) === String(salaryForm.payment_method_id));
        const keepBankFields = !!selectedPm?.requires_account_number;
        payload.salary_data = {
          ...salaryForm,
          amount: parseFloat(salaryForm.amount),
          currency_id: parseInt(salaryForm.currency_id),
          payment_method_id: salaryForm.payment_method_id ? parseInt(salaryForm.payment_method_id) : null,
          bank_id: keepBankFields && salaryForm.bank_id ? parseInt(salaryForm.bank_id) : null,
          account_number: keepBankFields ? salaryForm.account_number : null,
          account_title: keepBankFields ? salaryForm.account_title : null,
        };
      }
      const res = await api.put(`/api/onboarding/${id}/hr-data`, payload);
      setRequest(res.data.data);
      toast.success('HR data saved');
      // Refresh audit logs
      const auditRes = await api.get(`/api/onboarding/${id}/audit`);
      setAuditLogs(auditRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save HR data');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setSaving(true);
    try {
      await api.put(`/api/onboarding/${id}/approve`, { review_comments: reviewComments });
      toast.success('Request approved and employee record created');
      navigate('/onboarding/requests');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setSaving(false);
      setShowApproveConfirm(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) { toast.error('Rejection reason is required'); return; }
    setSaving(true);
    try {
      await api.put(`/api/onboarding/${id}/reject`, { rejection_reason: rejectionReason, review_comments: reviewComments });
      toast.success('Request rejected');
      navigate('/onboarding/requests');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject request');
    } finally {
      setSaving(false);
    }
  };

  const canAct = request && ['pending_review', 'in_review'].includes(request.status);
  const canApprove = canAct && hrForm.position && hrForm.department_id && hrForm.hire_date && hrForm.employment_type;

  const deptOptions = departments.map(d => ({ value: String(d.id), label: d.name }));
  const empOptions = employees
    .filter(e => (e.employment_status || 'active') === 'active'
      && !['Intern', 'Trainee'].includes(e.employment_type))
    .map(e => ({ value: String(e.id), label: `${e.first_name} ${e.last_name}` }));
  const currencyOptions = currencies.map(c => ({ value: String(c.id), label: `${c.code} - ${c.name}` }));
  const pmOptions = paymentMethods.map(p => ({ value: String(p.id), label: p.name }));
  const bankOptions = banks.map(b => ({ value: String(b.id), label: b.name }));

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Link to="/onboarding/requests" className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <ClipboardList className="h-6 w-6 text-primary-600" />
          <h1 className="text-xl font-bold text-gray-900">
            Onboarding Request — {request.first_name} {request.last_name}
          </h1>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getOnboardingBadgeClass(request.status)}`}>
          {formatStatusText(request.status)}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Data Panel (editable by reviewer) */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Personal Information</h2>
          {canAct ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                  <input value={personalForm.first_name} onChange={setPersonal('first_name')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                  <input value={personalForm.last_name} onChange={setPersonal('last_name')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email (read-only)</label>
                <input
                  type="email"
                  value={personalForm.email}
                  readOnly
                  className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600 cursor-not-allowed focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Work and personal emails are managed on the user record. Edit them via the Users &amp; Roles page if needed.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input value={personalForm.phone} onChange={setPersonal('phone')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">CNIC</label>
                  <input
                    value={personalForm.cnic}
                    onChange={(e) => setPersonalForm(prev => ({ ...prev, cnic: formatCNIC(e.target.value) }))}
                    placeholder="XXXXX-XXXXXXX-X"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="onboarding-review-dob" className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                  <input id="onboarding-review-dob" type="date" value={personalForm.date_of_birth} onChange={setPersonal('date_of_birth')} max={todayStr} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                  <Dropdown value={personalForm.gender} onChange={setPersonal('gender')}>
                    <option value="">Select</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </Dropdown>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                <textarea value={personalForm.address} onChange={setPersonal('address')} rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                  <input value={personalForm.city} onChange={setPersonal('city')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
                  <SearchableSelect
                    value={personalForm.country}
                    onChange={(val) => setPersonalForm(prev => ({ ...prev, country: val }))}
                    options={COUNTRY_OPTIONS}
                    placeholder="Select country..."
                    disabled={!canAct}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none"
                  />
                </div>
              </div>

              <h3 className="font-medium text-gray-800 pt-2">Emergency Contact</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                  <input value={personalForm.emergency_contact_name} onChange={setPersonal('emergency_contact_name')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input value={personalForm.emergency_contact_phone} onChange={setPersonal('emergency_contact_phone')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Relationship</label>
                <input value={personalForm.emergency_contact_relationship} onChange={setPersonal('emergency_contact_relationship')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none" />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleSavePersonal}
                  disabled={savingPersonal}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {savingPersonal ? 'Saving...' : 'Save Personal Info'}
                </button>
              </div>
            </>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="First Name" value={request.first_name} />
                <Field label="Last Name" value={request.last_name} />
                <Field label="Email" value={request.email} />
                <Field label="Phone" value={request.phone} />
                <Field label="CNIC" value={request.cnic} />
                <Field label="Date of Birth" value={request.date_of_birth ? formatDateOnly(request.date_of_birth) : null} />
                <Field label="Gender" value={request.gender} />
                <Field label="City" value={request.city} />
                <Field label="Country" value={request.country} />
                <div className="col-span-2"><Field label="Address" value={request.address} /></div>
              </dl>
              <h3 className="font-medium text-gray-800 mt-5 mb-3">Emergency Contact</h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Name" value={request.emergency_contact_name} />
                <Field label="Phone" value={request.emergency_contact_phone} />
                <div className="col-span-2"><Field label="Relationship" value={request.emergency_contact_relationship} /></div>
              </dl>
            </>
          )}
        </div>

        {/* HR Data Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Employment Details</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Position <span className="text-red-500">*</span>
            </label>
            <input
              value={hrForm.position}
              onChange={setHr('position')}
              disabled={!canAct}
              data-testid="onboarding-review-field-position"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={hrForm.department_id}
              onChange={(val) => setHrForm(prev => ({ ...prev, department_id: val }))}
              options={deptOptions}
              placeholder="Select department..."
              disabled={!canAct}
              data-testid="onboarding-review-field-department"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
            <SearchableSelect
              value={hrForm.manager_id}
              onChange={(val) => setHrForm(prev => ({ ...prev, manager_id: val }))}
              options={empOptions}
              placeholder="Select manager..."
              disabled={!canAct}
              data-testid="onboarding-review-field-manager"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hire Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={hrForm.hire_date}
              onChange={setHr('hire_date')}
              disabled={!canAct}
              data-testid="onboarding-review-field-hire-date"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employment Type <span className="text-red-500">*</span>
            </label>
            <Dropdown
              value={hrForm.employment_type}
              onChange={setHr('employment_type')}
              disabled={!canAct}
              data-testid="onboarding-review-field-employment-type"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
            >
              <option value="">Select type...</option>
              {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Dropdown>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="taxable"
              checked={hrForm.taxable}
              onChange={setHr('taxable')}
              disabled={!canAct}
              data-testid="onboarding-review-field-taxable"
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
            />
            <label htmlFor="taxable" className="text-sm font-medium text-gray-700">Taxable Employee</label>
          </div>

          {/* Salary toggle */}
          {canAct && (
            <div>
              <button
                type="button"
                onClick={() => setShowSalaryFields(prev => !prev)}
                className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                {showSalaryFields ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                <span>{showSalaryFields ? 'Hide salary details' : 'Add salary details'}</span>
              </button>
            </div>
          )}

          {showSalaryFields && (
            <div className="border border-gray-200 rounded-md p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Salary</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                  <input
                    type="number"
                    value={salaryForm.amount}
                    onChange={setSal('amount')}
                    onWheel={(e) => e.target.blur()}
                    disabled={!canAct}
                    data-testid="onboarding-review-field-salary-amount"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
                  <SearchableSelect
                    value={salaryForm.currency_id}
                    onChange={(val) => setSalaryForm(prev => ({ ...prev, currency_id: val }))}
                    options={currencyOptions}
                    placeholder="Select currency..."
                    disabled={!canAct}
                    data-testid="onboarding-review-field-salary-currency"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Effective Date</label>
                <input
                  type="date"
                  value={salaryForm.effective_date}
                  onChange={setSal('effective_date')}
                  disabled={!canAct}
                  data-testid="onboarding-review-field-salary-effective-date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
                <SearchableSelect
                  value={salaryForm.payment_method_id}
                  onChange={(val) => setSalaryForm(prev => ({ ...prev, payment_method_id: val }))}
                  options={pmOptions}
                  placeholder="Select payment method..."
                  disabled={!canAct}
                  data-testid="onboarding-review-field-salary-payment-method"
                />
              </div>
              {(() => {
                const selectedPm = paymentMethods.find(pm => String(pm.id) === String(salaryForm.payment_method_id));
                if (!selectedPm?.requires_account_number) return null;
                return (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Bank</label>
                      <SearchableSelect
                        value={salaryForm.bank_id}
                        onChange={(val) => setSalaryForm(prev => ({ ...prev, bank_id: val }))}
                        options={bankOptions}
                        placeholder="Select bank..."
                        disabled={!canAct}
                        data-testid="onboarding-review-field-salary-bank"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Account Number</label>
                        <input
                          value={salaryForm.account_number}
                          onChange={setSal('account_number')}
                          disabled={!canAct}
                          data-testid="onboarding-review-field-salary-account-number"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Account Title</label>
                        <input
                          value={salaryForm.account_title}
                          onChange={setSal('account_title')}
                          disabled={!canAct}
                          data-testid="onboarding-review-field-salary-account-title"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                        />
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Documents */}
          <DocumentSubsection
            documents={documents}
            onChange={setDocuments}
            disabled={!canAct}
          />

          {/* Review comments */}
          {canAct && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Comments (optional)</label>
              <textarea
                value={reviewComments}
                onChange={e => setReviewComments(e.target.value)}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          {/* Action buttons */}
          {canAct && (
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                onClick={handleSaveHRData}
                disabled={saving}
                data-testid="onboarding-review-submit"
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Save HR Data
              </button>
              <button
                onClick={() => setShowApproveConfirm(true)}
                disabled={saving || !canApprove}
                title={!canApprove ? 'Fill in position, department, hire date, and employment type first' : ''}
                data-testid="onboarding-review-approve"
                className="px-4 py-2 bg-success-600 text-white text-sm font-medium rounded-md hover:bg-success-700 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => setShowRejectForm(prev => !prev)}
                disabled={saving}
                data-testid="onboarding-review-reject"
                className="px-4 py-2 bg-danger-600 text-white text-sm font-medium rounded-md hover:bg-danger-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          )}

          {/* Approve confirmation */}
          {showApproveConfirm && canApprove && (
            <div data-testid="onboarding-review-confirm-modal" className="border border-green-200 rounded-md p-4 space-y-3 bg-green-50">
              <h3 className="text-sm font-semibold text-green-800">Confirm Approval</h3>
              <p className="text-sm text-green-700">
                Approve this onboarding request and create an employee record?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleApprove}
                  disabled={saving}
                  data-testid="onboarding-review-confirm-yes"
                  className="px-4 py-2 bg-success-600 text-white text-sm font-medium rounded-md hover:bg-success-700 disabled:opacity-50"
                >
                  Confirm Approval
                </button>
                <button
                  onClick={() => setShowApproveConfirm(false)}
                  data-testid="onboarding-review-confirm-no"
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Rejection form */}
          {showRejectForm && canAct && (
            <div className="border border-red-200 rounded-md p-4 space-y-3 bg-red-50">
              <h3 className="text-sm font-semibold text-red-800">Reject Request</h3>
              <div>
                <label className="block text-xs font-medium text-red-700 mb-1">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  rows={3}
                  placeholder="Provide a reason for rejection..."
                  data-testid="onboarding-review-reject-reason"
                  className="w-full border border-red-300 rounded-md px-3 py-2 text-sm focus:outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReject}
                  disabled={saving}
                  className="px-4 py-2 bg-danger-600 text-white text-sm font-medium rounded-md hover:bg-danger-700 disabled:opacity-50"
                >
                  Confirm Rejection
                </button>
                <button
                  onClick={() => setShowRejectForm(false)}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Audit Log */}
      {auditLogs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Audit Log</h2>
          <ol className="relative border-l border-gray-200 space-y-4 ml-3">
            {auditLogs.map((log, idx) => (
              <li key={idx} className="ml-4">
                <div className="absolute w-3 h-3 bg-primary-400 rounded-full -left-1.5 mt-0.5 border border-white" />
                <div className="text-xs text-gray-500 mb-0.5">
                  {new Date(log.timestamp).toLocaleString()} &mdash; <span className="font-medium text-gray-700">{log.username}</span>
                </div>
                <div className="text-sm text-gray-800 font-medium capitalize">{log.action.replace(/_/g, ' ')}</div>
                {log.previous_status && log.new_status && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {formatStatusText(log.previous_status)} &rarr; {formatStatusText(log.new_status)}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

// One instance per record: see utils/remountOnId.js.
export default remountOnId(OnboardingReview);
